import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export interface UptimeDataItem {
  environment_id: string;
  environment_name: string;
  environment_type: string;
  date: string;
  uptime_percentage: number;
}

interface UptimeTrendsChartProps {
  data: UptimeDataItem[] | null;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number | null; color: string }[]; label?: string }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-sm text-gray-700 dark:text-gray-300" style={{ color: entry.color }}>
          {entry.name}: {entry.value == null ? 'N/A' : `${entry.value.toFixed(2)}%`}
        </p>
      ))}
    </div>
  );
};

export default function UptimeTrendsChart({ data }: UptimeTrendsChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Uptime Trends (Last 30 Days)
        </h3>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No uptime data available
        </div>
      </div>
    );
  }

  // Group data by environment
  const environmentsMap: Record<string, { name: string; type: string; data: { date: string; uptime: number }[] }> = {};
  data.forEach((item) => {
    if (!environmentsMap[item.environment_id]) {
      environmentsMap[item.environment_id] = {
        name: item.environment_name,
        type: item.environment_type,
        data: [],
      };
    }
    environmentsMap[item.environment_id].data.push({
      date: item.date,
      uptime: item.uptime_percentage,
    });
  });

  // Get all unique dates and sort them
  const allDates = Array.from(new Set(data.map(item => item.date))).sort((a, b) => a.localeCompare(b));

  // Create chart data by date - ensure all environments have entries for all dates
  const chartDataArray = allDates.map(date => {
    const dataPoint: Record<string, string | number | null> = {
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };

    Object.entries(environmentsMap).forEach(([_envId, env]) => {
      const envDataPoint = env.data.find(d => d.date === date);
      dataPoint[env.name] = envDataPoint?.uptime ?? null;
    });

    return dataPoint;
  });

  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
  ];

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Uptime Trends (Last 30 Days)
      </h3>
      <div style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          <LineChart data={chartDataArray}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis
              dataKey="date"
              className="text-gray-600 dark:text-gray-400"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              domain={[0, 100]}
              className="text-gray-600 dark:text-gray-400"
              tick={{ fontSize: 12 }}
              label={{ value: 'Uptime %', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {Object.values(environmentsMap).map((env, index) => (
              <Line
                key={env.name}
                type="monotone"
                dataKey={env.name}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
